import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { useSEO } from "@/lib/useSEO";
import { SEO_FOOTER_LINKS, SEO_PAGES } from "@/pages/seo/seoContent";
import {
  CursorClick, ShieldCheck, EyeSlash, UsersThree, Globe, Check, X, ArrowRight,
  Timer, Fire, Infinity, CursorClick as MouseIcon, Keyboard, Lightning, Lock, Prohibit, Plus,
} from "@phosphor-icons/react";

const HERO_BG =
  "https://images.unsplash.com/photo-1770486036751-e55247238964?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdsb3dpbmclMjBuZXR3b3JrJTIwZGFya3xlbnwwfHx8fDE3ODQxNzA3OTl8MA&ixlib=rb-4.1.0&q=85";
const IMG_FAMILY =
  "https://images.unsplash.com/photo-1629360021730-3d258452c425?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxwZXJzb24lMjBoZWxwaW5nJTIwZmFtaWx5JTIwbWVtYmVyJTIwd2l0aCUyMGxhcHRvcCUyMGF0JTIwaG9tZXxlbnwwfHx8fDE3ODU0NTY5MTN8MA&ixlib=rb-4.1.0&q=85";
const IMG_SMB =
  "https://images.unsplash.com/photo-1545184180-25d471fe75eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxzbWFsbCUyMGJ1c2luZXNzJTIwb3duZXIlMjB3b3JraW5nJTIwbGFwdG9wJTIwd2FybXxlbnwwfHx8fDE3ODU0NTY5MTN8MA&ixlib=rb-4.1.0&q=85";

const PLANS = [
  { key: "personal", name: "Free", price: "Free", blurb: "Perfect for helping out Mom & Dad.", features: ["1 computer", "1 user", "Full remote control", "3 Controlly Snap trials"] },
  { key: "solo", name: "Solo", price: "$8", blurb: "For freelancers & home-lab tinkerers.", features: ["5 computers", "1 user", "1 live session", "3 Controlly Snap trials"] },
  { key: "group", name: "Group", price: "$15", blurb: "For a couple of people sharing access.", features: ["15 computers", "2 users", "2 live sessions", "3 Controlly Snap trials"], highlighted: true },
  { key: "smallbiz", name: "Team", price: "$29", blurb: "Everything your growing shop needs.", features: ["30 computers", "3 users", "3 live sessions", "Unlimited Controlly Snap"] },
];

const FEATURES = [
  { icon: CursorClick, span: "md:col-span-12 lg:col-span-8 lg:row-span-2", title: "Full control, zero fuss", desc: "Take over any of your computers right from the browser — click, type, drag, scroll. Nothing to install on the machine you're sitting at. It just feels like you're there.", big: true },
  { icon: ShieldCheck, span: "md:col-span-6 lg:col-span-4 lg:row-span-2", title: "You're always in control", desc: "A clear on-screen banner shows whenever a machine is being viewed — and the person sitting there can cut the connection with one click. No silent takeovers, no scams.", big: true },
  { icon: EyeSlash, span: "md:col-span-6 lg:col-span-4", title: "Set it and forget it", desc: "The agent auto-starts at login, runs silently, and sits at 0 bandwidth until you actually connect." },
  { icon: Lightning, span: "md:col-span-6 lg:col-span-4", title: "Controlly Snap instant support", desc: "Help someone with no account in seconds: send a link, they run a one-time helper, you take over — then it removes itself. Perfect for supporting customers." },
  { icon: UsersThree, span: "md:col-span-6 lg:col-span-4", title: "Share with your people", desc: "Invite family or teammates and give each person access to just the machines they need." },
  { icon: Globe, span: "md:col-span-12 lg:col-span-4", title: "Reach it from anywhere", desc: "Any modern browser, any network. All your computers in one simple dashboard." },
];

const FAQS = [
  {
    q: "Is Controlly free remote desktop software?",
    a: "Yes. Controlly has a free-forever plan that lets you remotely control one computer from any browser, with full mouse and keyboard control and no credit card required. Paid plans start at $8/month when you need more computers or users.",
  },
  {
    q: "How is Controlly different from TeamViewer or AnyDesk?",
    a: "Controlly is built for individuals, families, and small businesses instead of large enterprises. You get fair, flat pricing from $8/month (no $500-a-year seats), no \u201Ccommercial use detected\u201D lockouts on personal use, and a single lightweight agent that just works \u2014 all from your browser.",
  },
  {
    q: "Can I access my computer from any web browser?",
    a: "Yes. Once you install the Controlly agent on a computer, you can view and fully control it in real time from any modern web browser \u2014 Chrome, Edge, Safari, or Firefox \u2014 on any network, with nothing to install on the device you're sitting at.",
  },
  {
    q: "Is remote access with Controlly secure?",
    a: "Security is built in: sessions are encrypted, each machine pairs with a one-time token, and a clear on-screen indicator shows whenever a computer is being viewed \u2014 the person at the machine can cut the connection with one click. No silent takeovers.",
  },
  {
    q: "Can I help someone who doesn't have a Controlly account?",
    a: "Yes \u2014 that's Controlly Snap. Start a session from your dashboard, send the person a link (or read them a 6-digit code), and they run a one-time helper on their computer. No signup, nothing permanent installed: the helper removes itself when you're done, and an on-screen banner shows the whole time. It's ideal for IT pros and shops supporting customers. Every plan includes 3 free Controlly Snap sessions to try; the Team plan gets unlimited.",
  },
  {
    q: "What operating systems does Controlly support?",
    a: "The Controlly agent runs on Windows, macOS, and Linux, and the control dashboard works in any modern browser. The Windows installer sets everything up automatically, including Python, and auto-starts the agent when the computer logs in.",
  },
  {
    q: "How do I set up Controlly on a computer?",
    a: "Sign up free, add a machine in your dashboard, and download the one-click installer. Run it on the computer you want to control \u2014 it installs the secure agent and starts automatically. The machine then shows up online in your dashboard, ready to control.",
  },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Landing() {
  const [offer, setOffer] = useState(null);
  const [, setTick] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();

  const goToSnap = () => {
    if (user) {
      navigate("/billing");
    } else {
      try { localStorage.setItem("controlly_next", "/billing"); } catch { /* ignore */ }
      navigate("/login");
    }
  };

  useSEO({
    title: "Controlly \u2014 Remote Desktop Software for Any Browser",
    description:
      "View and fully control your computers in real time from any browser. Free plan, secure one-time pairing, and fair pricing \u2014 remote desktop made simple.",
    path: "/",
  });

  useEffect(() => {
    api.get("/offer").then((r) => setOffer(r.data)).catch(() => {});
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const offerActive = offer?.active && offer?.plan;
  const countdown = (() => {
    if (!offer?.ends_at) return null;
    const ms = new Date(offer.ends_at).getTime() - Date.now();
    if (ms <= 0) return null;
    const s = Math.floor(ms / 1000);
    const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    const p = (n) => String(n).padStart(2, "0");
    return (d > 0 ? `${d}d ` : "") + `${p(h)}:${p(m)}:${p(sec)}`;
  })();

  return (
    <div className="min-h-screen bg-background text-foreground antialiased overflow-x-hidden">
      {offerActive && (
        <a
          href="#pricing"
          data-testid="offer-announcement-bar"
          className="fixed top-0 inset-x-0 z-[60] bg-primary text-primary-foreground text-xs sm:text-sm font-bold flex items-center justify-center gap-2 py-1.5 px-4 hover:bg-primary/90 transition-colors"
        >
          <Fire size={14} weight="fill" />
          Lifetime deal: {offer.plan_name} for a one-time ${offer.price}
          {countdown && <span className="font-mono">· ends in {countdown}</span>}
          <span className="hidden sm:inline">· only {offer.spots_left} left</span>
          <span className="underline underline-offset-2">Claim now</span>
        </a>
      )}
      {/* Navbar */}
      <header className={`fixed inset-x-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10 ${offerActive ? "top-7 sm:top-8" : "top-0"}`}>
        <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="flex items-center gap-2.5" data-testid="nav-logo">
            <img src="/controlly-icon.png" alt="Controlly" className="w-8 h-8 rounded-md" />
            <span className="font-heading text-lg font-extrabold tracking-tight">Controlly</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            <a href="#compare" className="hidden sm:block text-sm text-muted-foreground hover:text-white transition-colors">Why Controlly</a>
            <a href="#pricing" className="hidden sm:block text-sm text-muted-foreground hover:text-white transition-colors">Pricing</a>
            <Link to="/login" data-testid="nav-login-button" className="hidden sm:block text-sm text-muted-foreground hover:text-white transition-colors">Log in</Link>
            <Link
              to="/login"
              data-testid="nav-get-started-cta"
              className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-bold px-4 py-2 hover:bg-primary/90 hover:scale-[1.04] transition-all focus-visible:ring-2 focus-visible:ring-primary/50"
            >
              Start free <ArrowRight size={15} weight="bold" />
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative pt-36 pb-24 sm:pt-44 sm:pb-32">
        <div className="absolute inset-0 session-grid-bg opacity-40" />
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.14, maskImage: "radial-gradient(circle at 70% 30%, black, transparent 70%)" }} />
        <div className="absolute inset-0 grain-overlay opacity-[0.06] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">Remote desktop, reimagined</span>
            </motion.div>
            <motion.h1 {...fade(0.06)} className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tighter">
              Control every computer.<br />
              <span className="text-primary">From any browser.</span>
            </motion.h1>
            <motion.p {...fade(0.14)} className="mt-6 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
              Install a lightweight agent once, then view and fully control your machines in real time. Secure, silent, and always on.
            </motion.p>
            <motion.div {...fade(0.18)} className="mt-5 flex flex-wrap items-center gap-2.5" data-testid="hero-capability-chips">
              {[
                { icon: MouseIcon, label: "Mouse" },
                { icon: Keyboard, label: "Keyboard" },
                { icon: Lightning, label: "Everything" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="group inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] pl-2.5 pr-3.5 py-1.5 text-sm font-medium text-foreground/90 hover:border-primary/40 hover:bg-primary/[0.06] transition-colors"
                >
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-primary/10 text-primary group-hover:bg-primary/20 transition-colors">
                    <Icon size={14} weight="bold" />
                  </span>
                  {label}
                </span>
              ))}
            </motion.div>
            <motion.div {...fade(0.22)} className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to="/login"
                data-testid="hero-get-started-cta"
                className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold px-6 py-3 hover:scale-[1.04] transition-transform"
              >
                Get started free <ArrowRight size={17} weight="bold" />
              </Link>
              <a
                href="#pricing"
                data-testid="hero-see-pricing-cta"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 font-medium px-6 py-3 hover:bg-white/10 transition-colors"
              >
                See pricing
              </a>
            </motion.div>
            <motion.p {...fade(0.3)} className="mt-5 font-mono text-xs text-muted-foreground/70">
              Free forever plan · Google sign-in · Cancel anytime
            </motion.p>
            <motion.div {...fade(0.34)} data-testid="hero-trust-bar" className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
              {[
                { icon: Lock, label: "Encrypted sessions" },
                { icon: ShieldCheck, label: "One-time pairing tokens" },
                { icon: EyeSlash, label: "On-screen access indicator" },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-1.5">
                  <Icon size={15} weight="fill" className="text-emerald-400/80" />
                  {label}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Hero visual: mock connection panel */}
          <motion.div {...fade(0.18)} className="lg:col-span-5">
            <div className="relative rounded-2xl border border-white/10 bg-card/60 backdrop-blur-md p-5 shadow-[0_0_60px_rgba(37,99,235,0.10)]">
              <div className="flex items-center gap-1.5 mb-4">
                <span className="w-3 h-3 rounded-full bg-red-500/70" />
                <span className="w-3 h-3 rounded-full bg-amber-500/70" />
                <span className="w-3 h-3 rounded-full bg-emerald-500/70" />
                <span className="ml-auto font-mono text-[11px] text-emerald-400 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE
                </span>
              </div>
              <div className="session-grid-bg rounded-lg border border-white/10 aspect-video flex items-center justify-center">
                <CursorClick size={40} className="text-primary/70" weight="duotone" />
              </div>
              <div className="mt-4 space-y-2 font-mono text-[11px] text-muted-foreground">
                <div className="flex justify-between"><span>agent // office-pc</span><span className="text-emerald-400">connected</span></div>
                <div className="flex justify-between"><span>pairing token</span><span>expires 14:59</span></div>
                <div className="flex justify-between"><span>bandwidth (idle)</span><span>0 KB/s</span></div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Anti-incumbent comparison */}
      <section id="compare" className="relative py-20 sm:py-28 border-t border-white/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.p {...fade(0)} className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-3">Why Controlly</motion.p>
          <motion.h2 {...fade(0.05)} className="font-heading text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Built for you — not IT mega-corps.
          </motion.h2>
          <motion.p {...fade(0.1)} className="mt-3 text-muted-foreground max-w-xl">
            The big remote-access companies are built for giant enterprises with giant budgets. We're built for the person who just needs to get on a computer.
          </motion.p>

          <div className="mt-12 grid md:grid-cols-2 gap-5 lg:gap-6">
            <motion.div {...fade(0.05)} data-testid="compare-incumbents" className="rounded-2xl border border-white/10 bg-white/[0.02] p-7">
              <div className="flex items-center gap-2 text-muted-foreground font-heading font-bold text-lg mb-5">
                <Prohibit size={22} weight="bold" className="text-red-400/80" /> The big guys
              </div>
              <ul className="space-y-3.5">
                {["$500+ per year, per seat", "Confusing licensing you need a rep to explain", '"Commercial use detected" lockouts on personal use', "Bloated installers and cold ticket support"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <X size={17} weight="bold" className="text-red-400/70 mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...fade(0.12)} data-testid="compare-controlly" className="rounded-2xl border border-primary/25 bg-gradient-to-b from-primary/[0.07] to-card p-7 shadow-[0_8px_40px_rgba(37,99,235,0.08)]">
              <div className="flex items-center gap-2 font-heading font-bold text-lg mb-5">
                <img src="/controlly-icon.png" alt="" className="w-6 h-6 rounded" /> Controlly
              </div>
              <ul className="space-y-3.5">
                {["Fair, flat pricing from $8/mo", "One silent agent that just works", "You're always in control — on-screen indicator + one-click cut-off", "Free forever plan, real human support"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-white/90">
                    <Check size={17} weight="bold" className="text-primary mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Personas */}
      <section className="relative py-16 sm:py-20 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fade(0)} className="font-heading text-2xl lg:text-3xl font-bold tracking-tight max-w-2xl">
            Whoever you're helping, Controlly fits.
          </motion.h2>
          <div className="mt-8 grid md:grid-cols-2 gap-5 lg:gap-6">
            {[
              { img: IMG_FAMILY, tag: "The family IT hero", line: "Fix Mom's laptop, Grandpa's printer, and your buddy's PC — without driving across town." },
              { img: IMG_SMB, tag: "The small-business owner", line: "Jump onto the back-office computer or a teammate's machine from wherever you are." },
            ].map((p) => (
              <motion.div key={p.tag} {...fade(0.08)} data-testid={`persona-${p.tag.includes("family") ? "family" : "smb"}`} className="group relative rounded-2xl overflow-hidden border border-white/10 h-64">
                <img src={p.img} alt={p.tag} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-80 group-hover:scale-[1.03] transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
                <div className="absolute bottom-0 p-6">
                  <div className="inline-block rounded-full bg-primary text-primary-foreground text-xs font-bold px-3 py-1 mb-2">{p.tag}</div>
                  <p className="text-sm text-white/90 max-w-xs leading-relaxed">{p.line}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-20 sm:py-28 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.p {...fade(0)} className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-3">What you get</motion.p>
          <motion.h2 {...fade(0.05)} className="font-heading text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Powerful where it counts. Simple everywhere else.
          </motion.h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5 lg:gap-6 auto-rows-[minmax(0,1fr)]">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.title}
                  {...fade(0.05 * i)}
                  data-testid={`feature-${i}`}
                  className={`${f.span} group rounded-2xl border border-white/10 bg-card/40 hover:bg-card/80 hover:border-primary/20 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(37,99,235,0.08)] transition-all p-7 flex flex-col`}
                >
                  <div className="w-11 h-11 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5">
                    <Icon size={22} weight="duotone" className="text-primary" />
                  </div>
                  <h3 className={`font-heading font-bold ${f.big ? "text-2xl" : "text-lg"} tracking-tight`}>{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed max-w-md">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative py-20 sm:py-28 border-t border-white/10 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.p {...fade(0)} className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-3">Pricing</motion.p>
          <motion.h2 {...fade(0.05)} className="font-heading text-3xl lg:text-4xl font-bold tracking-tight max-w-2xl">
            Honest pricing. No surprises.
          </motion.h2>
          <motion.p {...fade(0.1)} className="mt-3 text-muted-foreground max-w-xl">Every plan includes real-time control and the secure agent. Start free, upgrade only when you need more computers or people.</motion.p>
          <motion.p {...fade(0.12)} data-testid="pricing-currency-note" className="mt-2 font-mono text-xs uppercase tracking-wider text-muted-foreground/70">All prices in USD</motion.p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5 items-stretch">
            {PLANS.map((p, i) => {
              const isLifetime = offerActive && p.key === offer.plan;
              if (isLifetime) {
                return (
                  <motion.div
                    key={p.key}
                    {...fade(0.05 * i)}
                    data-testid="lifetime-offer-card"
                    className="relative rounded-2xl p-6 flex flex-col tracing-beam bg-gradient-to-b from-primary/[0.10] to-card lg:-mt-6 lg:mb-[-1.5rem] ring-1 ring-primary/30 animate-offer-glow"
                  >
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 flex items-center gap-1 whitespace-nowrap">
                      <Fire size={12} weight="fill" /> Limited time
                    </span>
                    <h3 className="font-heading text-lg font-bold mt-1">{p.name} <span className="text-primary">Lifetime</span></h3>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-3">Pay once. Yours forever.</p>
                    <div className="mb-1 flex items-baseline gap-2">
                      <span className="font-mono text-4xl font-extrabold tracking-tight text-primary">${offer.price}</span>
                      <span className="text-xs text-muted-foreground">one-time</span>
                    </div>
                    <div className="mb-3 text-sm text-muted-foreground">
                      <span className="line-through decoration-red-400/70">${offer.regular_price}/mo</span>
                      <span className="ml-2 text-emerald-400 font-medium">no monthly, ever</span>
                    </div>
                    {countdown && (
                      <div data-testid="offer-countdown" className="mb-2 flex items-center gap-1.5 rounded-md bg-black/40 border border-primary/20 px-2.5 py-1.5 font-mono text-sm text-primary">
                        <Timer size={15} weight="bold" /> Ends in {countdown}
                      </div>
                    )}
                    <div data-testid="offer-spots" className="mb-4 text-xs font-mono text-red-400">
                      Only {offer.spots_left} of {offer.spots_total} licenses left
                    </div>
                    <ul className="space-y-2 mb-6 flex-1">
                      {p.features.map((feat) => (
                        <li key={feat} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check size={15} weight="bold" className="text-primary mt-0.5 shrink-0" /> {feat}
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/login"
                      data-testid="lifetime-offer-cta"
                      className="mt-auto text-center rounded-full font-bold text-sm px-4 py-2.5 bg-primary text-primary-foreground hover:scale-[1.04] transition-transform"
                    >
                      Claim lifetime deal
                    </Link>
                    <Link
                      to="/login"
                      data-testid="lifetime-subscribe-monthly"
                      className="mt-2 text-center text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                    >
                      or subscribe monthly at ${offer.regular_price}/mo instead
                    </Link>
                  </motion.div>
                );
              }
              return (
                <motion.div
                  key={p.key}
                  {...fade(0.05 * i)}
                  className={`relative rounded-2xl p-6 flex flex-col transition-all hover:-translate-y-1 ${p.highlighted && !offerActive ? "tracing-beam bg-card ring-1 ring-primary/40 shadow-[0_8px_40px_rgba(37,99,235,0.14)] lg:-mt-4 lg:mb-[-1rem]" : "border border-white/10 bg-card/40"}`}
                >
                  {p.highlighted && !offerActive && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider px-3 py-1">Most popular</span>
                  )}
                  {p.key === "personal" && (
                    <span data-testid="free-for-life-badge" className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-emerald-500 text-black text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 flex items-center gap-1 whitespace-nowrap">
                      <Infinity size={12} weight="bold" /> Free for life
                    </span>
                  )}
                  <h3 className="font-heading text-lg font-bold">{p.name}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5 mb-4">{p.blurb}</p>
                  <div className="flex items-baseline gap-1 mb-5">
                    <span className="font-mono text-3xl font-extrabold tracking-tight">{p.price}</span>
                    {p.price !== "Free" && <span className="text-muted-foreground text-sm">/mo</span>}
                    {p.key === "personal" && <span className="text-emerald-400 text-sm font-medium">forever</span>}
                  </div>
                  <ul className="space-y-2 mb-6 flex-1">
                    {p.features.map((feat) => (
                      <li key={feat} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check size={15} weight="bold" className={`mt-0.5 shrink-0 ${p.highlighted ? "text-primary" : "text-primary/80"}`} /> {feat}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/login"
                    data-testid={`pricing-plan-cta-${p.key}`}
                    className={`mt-auto text-center rounded-full font-semibold text-sm px-4 py-2.5 transition-transform hover:scale-[1.03] ${p.highlighted ? "bg-primary text-primary-foreground hover:bg-primary/90" : "border border-white/15 bg-white/5 hover:bg-white/10"}`}
                  >
                    {p.key === "personal" ? "Start free" : `Choose ${p.name}`}
                  </Link>
                </motion.div>
              );
            })}
          </div>
          <motion.p {...fade(0.1)} className="mt-8 text-sm text-muted-foreground">
            Hate subscriptions? A one-time <span className="text-primary font-medium">Lifetime deal</span> drops now and then — grab it when you see the banner up top.
          </motion.p>

          <motion.div {...fade(0.15)} data-testid="snaplink-callout"
            onClick={goToSnap}
            role="button" tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goToSnap(); } }}
            className="group mt-12 rounded-2xl border border-primary/30 bg-primary/5 p-6 sm:p-8 text-left cursor-pointer transition-all hover:border-primary/60 hover:bg-primary/10 hover:shadow-[0_0_40px_-12px_rgba(37,120,255,0.5)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60">
            <div className="flex items-center gap-2 mb-2">
              <Lightning size={20} weight="fill" className="text-primary" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Controlly Snap</span>
            </div>
            <h3 className="font-heading text-2xl font-bold tracking-tight">Help anyone in seconds — no account needed</h3>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              Send a customer a link (or read them a 6-digit code). They run a one-time helper — nothing to sign up for, nothing left behind — and you're on their screen instantly. When you're done, it removes itself. Built for IT pros and small shops.
            </p>
            <div className="mt-5 grid sm:grid-cols-3 gap-4">
              {[
                ["No signup for them", "Your customer never creates an account or installs anything permanent."],
                ["Self-cleaning helper", "The one-time helper stops and deletes itself the moment the session ends."],
                ["Included on every plan", "3 free Controlly Snap sessions to try on any plan — unlimited on Team."],
              ].map(([t, d]) => (
                <div key={t} className="flex items-start gap-2">
                  <Check size={16} weight="bold" className="text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-semibold text-foreground">{t}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{d}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-bold px-5 py-2.5 transition-transform group-hover:scale-[1.03]" data-testid="snaplink-cta-button">
              {user ? "Get Controlly Snap" : "Get started with Controlly Snap"}
              <ArrowRight size={17} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-20 sm:py-28 border-t border-white/10 scroll-mt-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <motion.p {...fade()} className="font-mono text-xs uppercase tracking-[0.2em] text-primary mb-3">FAQ</motion.p>
          <motion.h2 {...fade(0.05)} className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">Questions, answered.</motion.h2>
          <motion.p {...fade(0.1)} className="mt-3 text-muted-foreground">Everything you need to know about controlling your computers with Controlly.</motion.p>
          <div className="mt-10 space-y-3" data-testid="faq-list">
            {FAQS.map((f, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                className="group rounded-xl border border-white/10 bg-card/40 px-5 py-4 open:bg-card/70 transition-colors"
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none font-heading font-semibold text-base sm:text-lg [&::-webkit-details-marker]:hidden">
                  <span>{f.q}</span>
                  <Plus size={18} weight="bold" className="shrink-0 text-primary transition-transform duration-300 group-open:rotate-45" />
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative border-t border-white/10 bg-black/60 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <Link to="/" data-testid="footer-logo-link" className="flex items-center gap-2.5 mb-4 w-fit group">
              <img src="/controlly-icon.png" alt="Controlly" className="w-9 h-9 rounded-md group-hover:scale-105 transition-transform" />
              <span className="font-heading text-2xl font-extrabold tracking-tight">Controlly</span>
            </Link>
            <p className="text-sm text-muted-foreground max-w-sm">Remote access without the enterprise price tag. For families, freelancers, and small businesses.</p>
          </div>
          <div className="md:text-right flex flex-col md:items-end gap-3">
            <Link to="/login" data-testid="footer-get-started-cta" className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-sm font-bold px-5 py-2.5 hover:bg-primary/90 hover:scale-[1.04] transition-all w-fit">
              Start free <ArrowRight size={15} weight="bold" />
            </Link>
            <a href="mailto:contact@getcontrolly.com" className="text-sm text-muted-foreground hover:text-white transition-colors" data-testid="footer-support-email">contact@getcontrolly.com</a>
            <Link to="/support" data-testid="footer-enter-code-link" className="text-sm text-primary hover:text-white transition-colors font-medium">Have a support code? Enter it here →</Link>
            <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground/70 mt-2 md:justify-end">
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <Link to="/login" className="hover:text-white transition-colors">Log in</Link>
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy</Link>
              <Link to="/refund-policy" className="hover:text-white transition-colors" data-testid="footer-refund-link">Refunds</Link>
              <Link to="/acceptable-use" className="hover:text-white transition-colors" data-testid="footer-aup-link">Acceptable Use</Link>
              <Link to="/cookies" className="hover:text-white transition-colors" data-testid="footer-cookies-link">Cookies</Link>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-12 pt-8 border-t border-white/5" data-testid="footer-explore-section">
          <h2 className="font-heading text-sm font-bold uppercase tracking-wider text-white/70 mb-4">Explore Controlly</h2>
          <div className="flex flex-wrap gap-x-5 gap-y-2.5">
            {SEO_FOOTER_LINKS.map((l) => (
              <Link
                key={l.slug}
                to={SEO_PAGES[l.slug].path}
                data-testid={`landing-footer-seo-link-${l.slug}`}
                className="text-sm text-muted-foreground/80 hover:text-white transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-12 pt-6 border-t border-white/5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-muted-foreground/60">
          <span>© {new Date().getFullYear()} Controlly. All rights reserved.</span>
          <span className="font-mono">getcontrolly.com</span>
        </div>
      </footer>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </div>
  );
}
